"use client";

import React, { useState, useEffect, useCallback } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import {
  FaFilePdf,
  FaSearchPlus,
  FaSearchMinus,
  FaSyncAlt,
  FaTimes,
  FaDownload,
  FaPrint,
  FaExternalLinkAlt,
} from "react-icons/fa";
import useApiClient from "../../../hooks/useApiClient";

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

/**
 * PdfPreviewPanel - A reusable PDF preview component
 * 
 * @param {string} endpoint - The API endpoint to fetch the PDF from
 * @param {string} documentId - The document ID to fetch
 * @param {string} title - Title for the preview panel
 * @param {boolean} isVisible - Whether the panel should be visible
 * @param {function} onClose - Callback when panel is closed
 * @param {string} downloadFileName - Optional filename for download
 */
const PdfPreviewPanel = ({
  endpoint,
  documentId,
  title = "Previzualizare Document",
  isVisible = true,
  onClose,
  downloadFileName = "document.pdf",
}) => {
  const apiClient = useApiClient();
  const [pdfUrl, setPdfUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [scale, setScale] = useState(0.8);

  const fetchPdf = useCallback(async () => {
    if (!endpoint || !documentId) {
      setError("Nu s-a specificat documentul");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Build the full endpoint URL
      const url = endpoint.replace("{id}", documentId);
      
      const response = await apiClient.get(url, { responseType: "blob" });

      if (response) {
        // Cleanup previous URL before creating new one
        setPdfUrl((prevUrl) => {
          if (prevUrl) URL.revokeObjectURL(prevUrl);
          const blob = new Blob([response], { type: "application/pdf" });
          return URL.createObjectURL(blob);
        });
      }
    } catch (err) {
      console.error("Error fetching PDF:", err);
      setError("Nu s-a putut încărca documentul PDF");
    } finally {
      setIsLoading(false);
    }
  }, [endpoint, documentId, apiClient]);

  useEffect(() => {
    if (isVisible && documentId) {
      fetchPdf();
    }

    // Cleanup on unmount - we need to capture the current pdfUrl value
    const currentPdfUrl = pdfUrl;
    return () => {
      if (currentPdfUrl) {
        URL.revokeObjectURL(currentPdfUrl);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, documentId, fetchPdf]);

  const handleDownload = () => {
    if (!pdfUrl) return;
    const link = document.createElement("a");
    link.href = pdfUrl;
    link.download = downloadFileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePrint = () => {
    if (!pdfUrl) return;
    const printWindow = window.open(pdfUrl, "_blank");
    if (printWindow) {
      printWindow.addEventListener("load", () => {
        printWindow.print();
      });
    }
  };

  const handleOpenInNewTab = () => {
    if (!pdfUrl) return;
    window.open(pdfUrl, "_blank");
  };

  if (!isVisible) return null;

  return (
    <div className="flex flex-col h-full bg-gray-100 dark:bg-gray-900 border-l border-gray-200 dark:border-gray-700">
      {/* Header */}
      <div className="flex items-center justify-between px-4 h-14 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
        <div className="flex items-center gap-2">
          <FaFilePdf className="w-4 h-4 text-red-500" />
          <h3 className="font-semibold text-gray-900 dark:text-white text-sm">
            {title}
          </h3>
        </div>
        <div className="flex items-center gap-1">
          {/* Zoom Controls */}
          {pdfUrl && !isLoading && (
            <div className="flex items-center gap-1 mr-2 border-r border-gray-200 dark:border-gray-600 pr-2">
              <button
                onClick={() => setScale((s) => Math.max(0.3, s - 0.1))}
                className="p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                title="Micșorează"
              >
                <FaSearchMinus className="w-3 h-3" />
              </button>
              <span className="text-xs text-gray-500 w-10 text-center font-medium">
                {Math.round(scale * 100)}%
              </span>
              <button
                onClick={() => setScale((s) => Math.min(2.0, s + 0.1))}
                className="p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                title="Mărește"
              >
                <FaSearchPlus className="w-3 h-3" />
              </button>
            </div>
          )}
          
          {/* Action buttons */}
          {pdfUrl && !isLoading && (
            <>
              <button
                onClick={handleDownload}
                className="p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                title="Descarcă"
              >
                <FaDownload className="w-3 h-3" />
              </button>
              <button
                onClick={handlePrint}
                className="p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                title="Printează"
              >
                <FaPrint className="w-3 h-3" />
              </button>
              <button
                onClick={handleOpenInNewTab}
                className="p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                title="Deschide în tab nou"
              >
                <FaExternalLinkAlt className="w-3 h-3" />
              </button>
            </>
          )}
          
          <button
            onClick={fetchPdf}
            disabled={isLoading}
            className="p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
            title="Reîncarcă"
          >
            <FaSyncAlt className={`w-3 h-3 ${isLoading ? "animate-spin" : ""}`} />
          </button>
          
          {onClose && (
            <button
              onClick={onClose}
              className="p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors ml-1"
              title="Închide previzualizarea"
            >
              <FaTimes className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>

      {/* PDF Content */}
      <div className="flex-1 overflow-auto bg-gray-200 dark:bg-gray-900">
        <div className="min-h-full flex justify-center py-4 px-4">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center h-96">
              <div className="relative">
                <div className="w-12 h-12 border-3 border-purple-200 rounded-full"></div>
                <div className="absolute inset-0 w-12 h-12 border-3 border-transparent border-t-purple-600 rounded-full animate-spin"></div>
              </div>
              <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">Se încarcă documentul...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-96">
              <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-3">
                <FaTimes className="w-6 h-6 text-red-500" />
              </div>
              <p className="text-sm text-red-600 dark:text-red-400 text-center px-4">{error}</p>
              <button
                onClick={fetchPdf}
                className="mt-3 px-3 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                Încearcă din nou
              </button>
            </div>
          ) : pdfUrl ? (
            <Document
              file={pdfUrl}
              onLoadSuccess={({ numPages }) => setNumPages(numPages)}
              onLoadError={(error) => {
                console.error("Error loading PDF:", error);
                setError("Nu s-a putut încărca PDF-ul");
              }}
              loading={
                <div className="flex flex-col items-center justify-center h-96">
                  <div className="w-10 h-10 border-2 border-purple-200 border-t-purple-600 rounded-full animate-spin"></div>
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Se încarcă...</p>
                </div>
              }
              className="flex flex-col items-center gap-4"
            >
              {Array.from(new Array(numPages || 1), (_, index) => (
                <div
                  key={`page_${index + 1}`}
                  className="bg-white shadow-xl rounded-sm overflow-hidden"
                  style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.15)" }}
                >
                  <Page
                    pageNumber={index + 1}
                    scale={scale}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                  />
                </div>
              ))}
            </Document>
          ) : (
            <div className="flex flex-col items-center justify-center h-96">
              <div className="w-16 h-16 rounded-xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center mb-3">
                <FaFilePdf className="w-8 h-8 text-purple-600 dark:text-purple-400" />
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Documentul se va încărca automat</p>
            </div>
          )}
        </div>
      </div>

      {/* Footer with page info */}
      {pdfUrl && numPages && (
        <div className="flex items-center justify-center px-4 py-2 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {numPages} {numPages === 1 ? "pagină" : "pagini"}
          </span>
        </div>
      )}
    </div>
  );
};

export default PdfPreviewPanel;
